import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/common/stores/authStore';
import './Sidebar.css';

interface NavItem {
  label: string;
  href: string;
  icon?: string;
  requiredRole?: string;
}

const Sidebar: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Update CSS variable when sidebar state changes
  React.useEffect(() => {
    const sidebarWidth = isSidebarOpen ? '280px' : '80px';
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
  }, [isSidebarOpen]);

  const handleLogout = async () => {
    await logout();
    void navigate('/');
  };

  // Check if user has admin role (with fallbacks)
  const isAdmin = (() => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    if (user.roles && Array.isArray(user.roles) && user.roles.includes('ADMIN')) return true;
    if (user.userRoles && Array.isArray(user.userRoles)) {
      return user.userRoles.some(ur => ur.role?.name === 'ADMIN');
    }
    return false;
  })();


  const navItems: NavItem[] = isAdmin
    ? [
        { label: 'Dashboard', href: '/admin', icon: '📊' },
        { label: 'Users', href: '/admin/users', icon: '👥' },
        { label: 'SubAccounts', href: '/admin/subaccounts', icon: '🏦' },
        { label: 'Payments', href: '/admin/payments', icon: '💰' },
        { label: 'Payouts', href: '/admin/payouts', icon: '📤' },
        { label: 'Disputes', href: '/admin/disputes', icon: '⚖️' },
        { label: 'Reports', href: '/admin/reports', icon: '🚩' },
        { label: 'Subscriptions', href: '/admin/platform-subscriptions', icon: '💳' },
        { label: 'Groups', href: '/admin/groups', icon: '👥' },
        { label: 'Settlements', href: '/admin/settlements', icon: '🤝' },
        { label: 'Notifications', href: '/admin/notifications', icon: '📣' },
        { label: 'Email Tester', href: '/admin/email-tester', icon: '✉️' },
      ]
    : [
        { label: 'Home', href: '/home', icon: '🏠' },
        { label: 'Browse', href: '/browse', icon: '🛍️' },
        { label: 'My Orders', href: '/orders', icon: '📦' },
        { label: 'My Subscriptions', href: '/account/subscriptions', icon: '🔄' },
        { label: 'Messages', href: '/messages', icon: '💬' },
      ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <aside className={`sidebar ${isSidebarOpen ? 'sidebar--open' : 'sidebar--closed'}`}>
      {/* Logo/Brand Section */}
      <div
        className="sidebar__header"
      >
        <RouterLink to="/home" className="sidebar__logo">
          <span className="sidebar__logo-text"
          onMouseEnter={() => !isSidebarOpen ? setIsSidebarOpen(true) : null}
          >EvenSteven</span>
        </RouterLink>

        {isSidebarOpen && (
          <button
            className="sidebar__toggle"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            ←
          </button>
        )}

      </div>

      {/* Navigation Items */}
      <nav className="sidebar__nav">
        {navItems.map((item) => (
          <RouterLink
            key={item.href}
            to={item.href}
            className={`sidebar__nav-item ${
              isActive(item.href) ? 'sidebar__nav-item--active' : ''
            }`}
            onClick={() => setIsProfileOpen(false)}
          >
            {item.icon && <span className="sidebar__nav-icon">{item.icon}</span>}
            {isSidebarOpen && <span className="sidebar__nav-label">{item.label}</span>}
          </RouterLink>
        ))}



      </nav>

      {/* Bottom Profile Section */}
      <div className="sidebar__footer">
        <div className="sidebar__profile-wrapper">
          <button
            className="sidebar__profile-btn"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            aria-label="Toggle profile menu"
          >
            <div className="sidebar__profile-avatar">
              {user?.name?.[0]?.toUpperCase() || ':)'}
            </div>
            {isSidebarOpen && (
              <>
                <div className="sidebar__profile-info">
                  <div className="sidebar__profile-name">
                    {user?.name}
                  </div>
                  <div className="sidebar__profile-email">{user?.email}</div>
                </div>
              </>
            )}
          </button>

          {/* Profile Dropdown */}
          {isProfileOpen && isSidebarOpen && (
            <div className="sidebar__profile-dropdown">
              <RouterLink to="/profile" className="sidebar__profile-item">
                Profile
              </RouterLink>
              <RouterLink to="/settings" className="sidebar__profile-item">
                Settings
              </RouterLink>
              <button
                onClick={handleLogout}
                className="sidebar__profile-item sidebar__profile-item--logout"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
